// Assembled entity/relationship graph — the single source the selectors read.
// Combines the Phase-1 legacy content (curated ISO/NIST controls, NIST↔ISO
// maps-to) with the Phase-2 generated layer (ATT&CK anchor entities + sourced
// NIST↔ATT&CK `mitigates`), normalising ATT&CK ids onto the anchor (ADR-9).

import {
  Entity,
  Relationship,
  DomainId,
  validateEntities,
  validateRelationships,
} from './schema';
import { legacyEntities, legacyRelationships } from './adapters/legacy';
import { ATTACK_ENTITIES } from './generated/attack.generated';
import {
  NIST_CONTROL_STUBS,
  NIST_MITIGATES,
  NIST_ATTACK_META,
} from './generated/nist-attack.generated';
import { D3FEND_ENTITIES, D3FEND_COUNTERS, D3FEND_META } from './generated/d3fend.generated';
import { KEV_CVES, KEV_ENABLES, KEV_META } from './generated/kev.generated';
import { CIS_ENTITIES } from './generated/cis.generated';
import { normaliseAttackId } from '../lib/attack-ids';
// @ts-ignore — legacy JS module
import { domainTTPs } from './mitre/attack-mappings';

// ZT-pillar domains per technique, from the curated pillar mapping (normalised).
const techDomains = new Map<string, DomainId[]>();
{
  const acc = new Map<string, Set<DomainId>>();
  for (const [domain, ids] of Object.entries(domainTTPs as Record<string, string[]>)) {
    for (const raw of ids) {
      const id = normaliseAttackId(raw);
      if (!acc.has(id)) acc.set(id, new Set());
      acc.get(id)!.add(domain as DomainId);
    }
  }
  acc.forEach((set, id) => techDomains.set(id, Array.from(set)));
}

const entityById = new Map<string, Entity>();

// Curated ISO + NIST controls from the legacy adapter (rich metadata, domains).
for (const e of legacyEntities) {
  if (e.frameworkId === 'iso-27001-2022' || e.frameworkId === 'nist-800-53-r5') {
    entityById.set(e.id, e);
  }
}

// CIS Controls v8.1 safeguards (control entities). No ATT&CK relationships yet —
// the CIS community mappings aren't in the repo (scaffold; ADR-7). Domain-agnostic.
for (const c of CIS_ENTITIES) {
  entityById.set(c.id, {
    id: c.id,
    frameworkId: 'cis-v8.1',
    kind: 'control',
    name: c.name,
    domains: [],
    tags: [],
    metadata: {
      family: c.family,
      securityFunction: c.securityFunction,
      implementationGroups: c.implementationGroups,
    },
  });
}

// NIST controls referenced only by the mapping (CP-*, MP-*, …) — fill the gaps.
for (const s of NIST_CONTROL_STUBS) {
  if (!entityById.has(s.id)) {
    entityById.set(s.id, {
      id: s.id,
      frameworkId: 'nist-800-53-r5',
      kind: 'control',
      name: s.name,
      domains: [],
      tags: [],
      metadata: { family: s.family, generated: true },
    });
  }
}

// ATT&CK techniques: the generated anchor set is the source of record. It
// supersedes the legacy hand-authored technique entities (whose detection/
// mitigation prose still serves the TTP detail pane via technique-details.js).
for (const a of ATTACK_ENTITIES) {
  entityById.set(a.id, {
    id: a.id,
    frameworkId: 'mitre-attack',
    kind: 'offensive-technique',
    name: a.name,
    domains: techDomains.get(a.id) ?? [],
    tags: [],
    metadata: { tactics: a.tactics, isSubtechnique: a.isSubtechnique, deprecated: a.deprecated },
  });
}

// D3FEND countermeasures (defensive-technique). First-class entities (ADR-3);
// they surface in the technique detail pane via their `counters` relationships,
// not via any field on a control. Domains are left empty — they are reached
// through the graph, not the pillar panes.
for (const c of D3FEND_ENTITIES) {
  entityById.set(c.id, {
    id: c.id,
    frameworkId: 'd3fend',
    kind: 'defensive-technique',
    name: c.name,
    domains: [],
    tags: [],
    metadata: { tactic: c.tactic, topTechnique: c.topTechnique },
  });
}

// CISA KEV CVEs (vulnerability). An active-exploitation overlay (ADR-4): they
// reach techniques through `enables`, driving the KEV badge + prioritisation.
for (const v of KEV_CVES) {
  entityById.set(v.id, {
    id: v.id,
    frameworkId: 'cisa-kev',
    kind: 'vulnerability',
    name: v.name,
    domains: [],
    tags: [],
    metadata: { vulnType: v.vulnType, snapshotDate: KEV_META.snapshotDate },
  });
}

// NIST↔ATT&CK `mitigates`, with uniform provenance from the dataset metadata.
// Pairs are deduped: distinct 16.1 ids can collapse onto one anchor id after
// normalisation, which would otherwise produce duplicate relationships.
const relationships: Relationship[] = [...legacyRelationships];
const unresolved = new Set<string>();
const seenMitigates = new Set<string>();
let mitigatesAdded = 0;
for (const pair of NIST_MITIGATES) {
  const targetId = normaliseAttackId(pair.targetId);
  const key = `${pair.sourceId}|${targetId}`;
  if (seenMitigates.has(key)) continue;
  seenMitigates.add(key);
  if (!entityById.has(targetId)) {
    // Single known case: T1521.003, revoked with no replacement. Stub it so the
    // relationship stays referentially intact rather than orphaning.
    unresolved.add(targetId);
    entityById.set(targetId, {
      id: targetId,
      frameworkId: 'mitre-attack',
      kind: 'offensive-technique',
      name: targetId,
      domains: [],
      tags: [],
      metadata: { unresolved: true },
    });
  }
  relationships.push({
    sourceId: pair.sourceId,
    targetId,
    type: 'mitigates',
    source: NIST_ATTACK_META.source,
    confidence: 'authoritative',
  });
  mitigatesAdded++;
}

// D3FEND `counters`: countermeasure → ATT&CK technique (intrinsic D3FEND ontology).
// All countered ids resolve onto the anchor; guard + dedupe defensively.
let countersAdded = 0;
const seenCounters = new Set<string>();
for (const pair of D3FEND_COUNTERS) {
  const targetId = normaliseAttackId(pair.attackId);
  if (!entityById.has(pair.sourceId) || !entityById.has(targetId)) continue;
  const key = `${pair.sourceId}|${targetId}`;
  if (seenCounters.has(key)) continue;
  seenCounters.add(key);
  relationships.push({
    sourceId: pair.sourceId,
    targetId,
    type: 'counters',
    source: D3FEND_META.source,
    confidence: 'authoritative',
  });
  countersAdded++;
}

// KEV `enables`: CVE → ATT&CK technique (sourced; never guessed).
let enablesAdded = 0;
const seenEnables = new Set<string>();
for (const pair of KEV_ENABLES) {
  const targetId = normaliseAttackId(pair.attackId);
  if (!entityById.has(pair.cveId) || !entityById.has(targetId)) continue;
  const key = `${pair.cveId}|${targetId}`;
  if (seenEnables.has(key)) continue;
  seenEnables.add(key);
  relationships.push({
    sourceId: pair.cveId,
    targetId,
    type: 'enables',
    source: KEV_META.source,
    confidence: 'authoritative',
  });
  enablesAdded++;
}

const entities: Entity[] = Array.from(entityById.values());

if (process.env.NODE_ENV !== 'production') {
  validateEntities(entities);
  validateRelationships(relationships);
}

/** Reconciliation stats for the version-skew integrity test. */
export const GRAPH_META = {
  entityCount: entities.length,
  relationshipCount: relationships.length,
  mitigatesRawCount: NIST_MITIGATES.length,
  mitigatesCount: mitigatesAdded,
  countersCount: countersAdded,
  enablesCount: enablesAdded,
  kevSnapshotDate: KEV_META.snapshotDate,
  unresolvedTargetCount: unresolved.size,
  unresolvedTargets: Array.from(unresolved),
} as const;

export { entities, relationships };
